import React from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  Icon, 
  Heading, 
  VStack,
  HStack,
  Button,
  Circle,
  Divider,
  useColorModeValue,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle, 
  Calendar, 
  Users,
  FileText,
  MessageSquare,
  Briefcase
} from 'lucide-react';
import NeuralBackground from '../../components/NeuralBackground';
import SlideContainer from '../../components/SlideContainer';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import JIMILogo from '../../components/JIMILogo';

// Next step component
const NextStep: React.FC<{
  title: string;
  description: string;
  timeline: string;
  icon: React.FC;
  color: string;
  index: number;
}> = ({ title, description, timeline, icon, color, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * index, duration: 0.5 }}
      style={{ width: "100%" }}
    >
      <Box 
        bg="white" 
        p={4} 
        borderRadius="md" 
        boxShadow="sm" 
        borderWidth="1px" 
        borderColor="gray.200"
        position="relative"
        overflow="hidden"
      >
        <Flex>
          <Circle size="40px" bg={`${color}15`} color={color} mr={4} flexShrink={0}>
            <Icon as={icon} boxSize={5} />
          </Circle>
          
          <Box flex="1">
            <Flex justify="space-between" align="center" mb={1}>
              <Text fontSize="md" fontWeight="bold" color="gray.700">
                {title}
              </Text>
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                {timeline}
              </Text>
            </Flex>
            <Text fontSize="sm" color="gray.600">
              {description}
            </Text>
          </Box>
        </Flex>
      </Box>
    </motion.div>
  );
};

const NextStepsSlide: React.FC = () => {
  // Colors
  const methodistBlue = "#0066CC";
  const methodistGreen = "#4A8B71";
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Next steps data
  const nextSteps = [
    {
      title: "Stakeholder Alignment",
      description: "Secure final approval from key stakeholders and establish governance committee for project oversight.",
      timeline: "Weeks 1-2",
      icon: Users,
      color: methodistBlue
    },
    {
      title: "Technical Planning",
      description: "Finalize technical architecture, security protocols, and integration requirements with IT department.",
      timeline: "Weeks 3-4",
      icon: Briefcase,
      color: methodistGreen
    },
    {
      title: "Development Kickoff",
      description: "Assemble development team, establish sprint schedule, and begin implementation of core infrastructure.",
      timeline: "Week 5",
      icon: Calendar,
      color: "#805AD5" // Purple
    },
    {
      title: "Clinician Focus Groups",
      description: "Conduct detailed focus groups with clinicians to refine user requirements and prioritize features.",
      timeline: "Weeks 6-8",
      icon: MessageSquare,
      color: "#DD6B20" // Orange
    },
    {
      title: "Project Documentation",
      description: "Create comprehensive project documentation including technical specifications and user guides.",
      timeline: "Ongoing",
      icon: FileText,
      color: "#718096" // Gray
    }
  ];
  
  // Immediate actions
  const immediateActions = [
    "Schedule executive committee presentation for final approval",
    "Identify IT department resources for technical implementation",
    "Establish project budget account and procurement processes",
    "Begin recruitment for key development team positions",
    "Create detailed communication plan for stakeholder updates"
  ];
  
  return (
    <MethodistSlide>
      <SlideContainer>
        <Box position="relative" height="100%" overflow="hidden">
          <Box position="absolute" top={0} right={0} bottom={0} left={0} zIndex={-1} opacity={0.05}>
            <NeuralBackground />
          </Box>
          
          <Flex direction="column" height="100%" p={6} maxHeight="100%" overflowY="auto"> 
            {/* Header */}
            <Flex align="center" mb={4}> 
              <JIMILogo height="40px" mr={4} />
              <Heading size="lg">Next Steps</Heading>
            </Flex>
            
            {/* Main content */}
            <Flex direction={{ base: "column", lg: "row" }} gap={6} flex="1" maxHeight="calc(100% - 80px)" overflowY="hidden"> 
              {/* Left side - Next Steps */}
              <Box flex="3" overflowY="auto"> 
                <VStack spacing={4} align="stretch" height="100%"> 
                  <Box bg="blue.50" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.100" mb={2}> 
                    <Text fontSize="sm" color="blue.800">
                      Following this presentation, we will begin the implementation process with the following key steps.
                      Each step has been carefully planned to ensure a smooth and successful deployment of the JIMI Wellness Platform.
                    </Text>
                  </Box>
                  
                  <VStack spacing={3} align="stretch">
                    {nextSteps.map((step, index) => (
                      <NextStep
                        key={index}
                        title={step.title}
                        description={step.description}
                        timeline={step.timeline}
                        icon={step.icon}
                        color={step.color}
                        index={index}
                      />
                    ))}
                  </VStack>
                </VStack>
              </Box>
              
              {/* Right side - Immediate Actions */}
              <Box flex="2" overflowY="auto"> 
                <VStack spacing={4} align="stretch" height="100%">
                  <Box bg={bgColor} borderRadius="lg" boxShadow="md" p={5} borderWidth="1px" borderColor={borderColor}>
                    <Heading size="md" mb={4} color={methodistBlue}> 
                      Immediate Actions
                    </Heading>
                    
                    <List spacing={3}>
                      {immediateActions.map((action, index) => (
                        <ListItem key={index} display="flex" alignItems="flex-start">
                          <ListIcon as={CheckCircle} color={methodistGreen} mt={1} />
                          <Text fontSize="sm" color="gray.700">
                            {action}
                          </Text>
                        </ListItem>
                      ))}
                    </List>
                    
                    <Divider my={4} />
                    
                    <VStack spacing={4} align="stretch">
                      <Heading size="sm" color={methodistBlue}>
                        Request for Approval
                      </Heading>
                      
                      <Text fontSize="sm" color="gray.600">
                        We are seeking approval to proceed with the implementation plan as outlined, 
                        including the allocation of the proposed budget and resources for the JIMI Wellness Platform.
                      </Text>
                      
                      <Button 
                        leftIcon={<Icon as={ArrowRight} />} 
                        colorScheme="blue" 
                        size="md"
                        alignSelf="flex-start"
                      >
                        Request Approval
                      </Button>
                    </VStack>
                  </Box>
                  
                  <Box bg="green.50" p={4} borderRadius="md" borderWidth="1px" borderColor="green.100">
                    <Heading size="sm" mb={3} color="green.700">
                      Key Contacts
                    </Heading>
                    
                    <VStack spacing={3} align="stretch">
                      <HStack spacing={3}>
                        <Circle size="32px" bg={`${methodistGreen}20`} color={methodistGreen} flexShrink={0}>
                          <Icon as={Users} boxSize={4} />
                        </Circle>
                        <Box>
                          <Text fontSize="sm" fontWeight="medium" color="green.700">
                            Project Sponsor
                          </Text>
                          <Text fontSize="xs" color="green.600">
                            Dr. Sarah Johnson, Chief Medical Officer
                          </Text>
                        </Box>
                      </HStack>
                      
                      <HStack spacing={3}>
                        <Circle size="32px" bg={`${methodistGreen}20`} color={methodistGreen} flexShrink={0}>
                          <Icon as={Briefcase} boxSize={4} />
                        </Circle>
                        <Box>
                          <Text fontSize="sm" fontWeight="medium" color="green.700">
                            Project Manager
                          </Text>
                          <Text fontSize="xs" color="green.600">
                            Michael Chen, Director of Digital Transformation
                          </Text>
                        </Box>
                      </HStack>
                      
                      <HStack spacing={3}>
                        <Circle size="32px" bg={`${methodistGreen}20`} color={methodistGreen} flexShrink={0}>
                          <Icon as={MessageSquare} boxSize={4} />
                        </Circle>
                        <Box>
                          <Text fontSize="sm" fontWeight="medium" color="green.700">
                            Technical Lead
                          </Text>
                          <Text fontSize="xs" color="green.600">
                            Priya Patel, Senior Solutions Architect
                          </Text>
                        </Box>
                      </HStack>
                    </VStack>
                  </Box>
                </VStack>
              </Box>
            </Flex>
            
            {/* Footer note */}
            <Box mt={4} p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.100"> 
              <Text fontSize="sm" color="blue.700" textAlign="center">
                <Icon as={Calendar} verticalAlign="middle" mr={1} />
                A detailed project plan with specific milestones and deliverables will be distributed 
                following approval of this initiative.
              </Text>
            </Box>
          </Flex>
        </Box>
      </SlideContainer>
    </MethodistSlide>
  );
};

export default NextStepsSlide;
